import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Edit, Trash2, Plus, Upload, X } from 'lucide-react';
import api from '../../utils/api';

interface Model {
  id: number;
  modele: string;
  annee: string;
  active: boolean;
  vehicles_count: number;
}

interface Brand {
  id: number;
  name: string;
  icon: string | File | null;
  vehicles_count: number;
  models_count: number;
  models?: Model[];
}

type SortOrder = 'asc' | 'desc' | null;

interface LazyParams {
  first: number;
  rows: number;
  page: number;
  sortField: string | null;
  sortOrder: SortOrder;
  filters: Record<string, any>;
}

interface ModelPagination {
  currentPage: number;
  totalPages: number;
  perPage: number;
  total: number;
}

export default function Brands() {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyParams, setLazyParams] = useState<LazyParams>({
    first: 0,
    rows: 15,
    page: 0,
    sortField: null,
    sortOrder: null,
    filters: {}
  });
  const [modelDialogVisible, setModelDialogVisible] = useState(false);
  const [editingModel, setEditingModel] = useState<Model | null>(null);
  const { t } = useTranslation();
  const [toast, setToast] = useState({ visible: false, severity: '', summary: '', detail: '' });
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [modelPagination, setModelPagination] = useState<ModelPagination>({
    currentPage: 1,
    totalPages: 1,
    perPage: 10,
    total: 0
  });

  useEffect(() => {
    fetchBrands();
  }, [lazyParams]);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await api.get('/brands', {
        params: {
          page: lazyParams.page + 1,
          per_page: lazyParams.rows,
          sort_field: lazyParams.sortField || undefined,
          sort_order: lazyParams.sortOrder,
          ...lazyParams.filters
        }
      });
      setBrands(response.data.data);
      setTotalRecords(response.data.total);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching brands:', error);
      showToast('error', t('error'), t('error_fetching_brands'));
      setLoading(false);
    }
  };

  const showToast = (severity: string, summary: string, detail: string) => {
    setToast({ visible: true, severity, summary, detail });
    setTimeout(() => setToast({ ...toast, visible: false }), 3000);
  };

  const openNew = () => {
    setEditingBrand({
      id: 0,
      name: '',
      icon: null,
      vehicles_count: 0,
      models_count: 0,
    });
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
    setEditingBrand(null);
  };

  const saveBrand = async () => {
    if (!editingBrand) return;

    try {
      const formData = new FormData();
      formData.append('name', editingBrand.name);

      const iconFile = fileInputRef.current?.files?.[0];
      if (iconFile) {
        formData.append('icon', iconFile);
      }

      if (editingBrand.id === 0) {
        await api.post('/brands', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showToast('success', t('success'), t('brand_created'));
      } else {
        formData.append('_method', 'PUT');
        await api.post(`/brands/${editingBrand.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showToast('success', t('success'), t('brand_updated'));
      }
      fetchBrands();
      hideDialog();
    } catch (error) {
      console.error('Error saving brand:', error);
      showToast('error', t('error'), t('error_saving_brand'));
    }
  };

  const editBrand = async (brand: Brand) => {
    try {
      const response = await api.get(`/brands/${brand.id}`);
      const brandData = response.data;
      const totalModels = brandData.models ? brandData.models.length : 0;
      const totalPages = Math.ceil(totalModels / modelPagination.perPage);

      loadModels(brandData.id,1);
      setModelPagination({
        currentPage: 1,
        totalPages,
        perPage: modelPagination.perPage,
        total: totalModels
      });
      setEditingBrand({
        ...brandData,
        models: brandData.models ? brandData.models.slice(0, modelPagination.perPage) : []
      });
      setDialogVisible(true);
    } catch (error) {
      console.error('Error fetching brand details:', error);
      showToast('error', t('error'), t('error_fetching_brand_details'));
    }
  };

  const confirmDeleteBrand = (brand: Brand) => {
    if (window.confirm(t('confirm_delete_brand'))) {
      deleteBrand(brand);
    }
  };

  const deleteBrand = async (brand: Brand) => {
    try {
      await api.delete(`/brands/${brand.id}`);
      showToast('success', t('success'), t('brand_deleted'));
      fetchBrands();
    } catch (error) {
      console.error('Error deleting brand:', error);
      showToast('error', t('error'), t('error_deleting_brand'));
    }
  };

  const onPage = (page: number) => {
    setLazyParams(prevState => ({ ...prevState, page, first: page * lazyParams.rows }));
  };

  const onSort = (field: string) => {
    const sortOrder = lazyParams.sortField === field && lazyParams.sortOrder === 'asc' ? 'desc' : 'asc';
    setLazyParams(prevState => ({ ...prevState, sortField: field, sortOrder }));
  };

  const onFilter = (field: string, value: string) => {
    setLazyParams(prevState => ({
      ...prevState,
      first: 0,
      page: 0,
      filters: { ...prevState.filters, [field]: value }
    }));
  };

  const handleIconUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && editingBrand) {
      setEditingBrand({ ...editingBrand, icon: file });
    }
  };

  const openNewModel = () => {
    setEditingModel({
      id: 0,
      modele: '',
      annee: '',
      active: true,
      vehicles_count: 0,
    });
    setModelDialogVisible(true);
  };

  const hideModelDialog = () => {
    setModelDialogVisible(false);
    setEditingModel(null);
  };

  const saveModel = async () => {
    if (!editingModel || !editingBrand) return;

    try {
      if (editingModel.id === 0) {
        await api.post(`/brands/${editingBrand.id}/models`, editingModel);
      loadModels(editingBrand.id, 1);

        showToast('success', t('success'), t('model_created'));
      } else {
        await api.put(`/models/${editingModel.id}`, editingModel);
      loadModels(editingBrand.id, 1);

        showToast('success', t('success'), t('model_updated'));
      }
      const updatedBrand = await api.get(`/brands/${editingBrand.id}`);
      setEditingBrand(updatedBrand.data);
      hideModelDialog();
    } catch (error) {
      console.error('Error saving model:', error);
      showToast('error', t('error'), t('error_saving_model'));
    }
  };

  const editModel = (model: Model) => {
    setEditingModel({ ...model });
    setModelDialogVisible(true);    

  };


  const confirmDeleteModel = (model: Model) => {
    if (window.confirm(t('confirm_delete_model'))) {
      deleteModel(model);
    }
  };

  const deleteModel = async (model: Model) => {
    if (!editingBrand) return;

    try {
      await api.delete(`/models/${model.id}`);
      showToast('success', t('success'), t('model_deleted'));
      const updatedBrand = await api.get(`/brands/${editingBrand.id}`);
      setEditingBrand(updatedBrand.data);
      loadModels(editingBrand.id, 1);

    } catch (error) {
      console.error('Error deleting model:', error);
      showToast('error', t('error'), t('error_deleting_model'));
    }
  };

  const loadModels = async (brandId: number, page = 1) => {
    console.log('LOADI',brandId);
    try {
      const response = await api.get(`/brands/${brandId}/models`, {
        params: {
          page,
          per_page: modelPagination.perPage
        }
      });
      setEditingBrand(prevBrand => {
        if (prevBrand) {
          return {
            ...prevBrand,
            models: response.data.data
          };
        }
        return null;
      });
      setModelPagination({
        currentPage: response.data.current_page,
        totalPages: response.data.last_page,
        perPage: response.data.per_page,
        total: response.data.total
      });
    } catch (error) {
      console.error('Error loading models:', error);
      showToast('error', t('error'), t('error_loading_models'));
    }
  };

  const handleModelPageChange = (page: number) => {
    if (editingBrand && editingBrand.models) {
      loadModels(editingBrand.id, page);
    }
  };

  return (
    <div className="p-6 min-h-screen bg-gray-100">
      {toast.visible && (
        <div className={`fixed top-4 right-4 p-4 rounded-md shadow-md ${
          toast.severity === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          <h3 className="font-bold">{toast.summary}</h3>
          <p>{toast.detail}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">{t('manage_brands')}</h2>
            <button
              onClick={openNew}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200"
            >
              <Plus className="inline-block mr-2" size={18} />
              {t('new_brand')}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('icon')}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => onSort('name')}
                >
                  {t('name')}
                  {lazyParams.sortField === 'name' && (
                    <span className="ml-2">
                      {lazyParams.sortOrder === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => onSort('vehicles_count')}
                >
                  {t('vehicles_count')}
                  {lazyParams.sortField === 'vehicles_count' && (
                    <span className="ml-2">
                      {lazyParams.sortOrder === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => onSort('models_count')}
                >
                  {t('models_count')}
                  {lazyParams.sortField === 'models_count' && (
                    <span className="ml-2">
                      {lazyParams.sortOrder === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">{t('loading')}</td>
                </tr>
              ) : brands.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">{t('no_brands_found')}</td>
                </tr>
              ) : (
                brands.map((brand) => (
                  <tr key={brand.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {brand.icon && (
                        <img 
                          src={`${process.env.REACT_APP_PIC_STORAGE}/${brand.icon}`} 
                          alt={brand.name} 
                          className="w-10 h-10 object-contain rounded-full bg-gray-100 p-1 border border-gray-200"
                        />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{brand.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {brand.vehicles_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {brand.models_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => editBrand(brand)}
                        className="text-indigo-600 hover:text-indigo-900 mr-2 focus:outline-none focus:underline"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => confirmDeleteBrand(brand)}
                        className="text-red-600 hover:text-red-900 focus:outline-none focus:underline"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between items-center p-4 border-t border-gray-200">
          <span className="text-sm text-gray-700">
            {t('showing')} {lazyParams.first + 1} - {Math.min(lazyParams.first + lazyParams.rows, totalRecords)} {t('of')} {totalRecords}
          </span>
          <div className="flex items-center">
            <button
              onClick={() => onPage(0)}
              disabled={lazyParams.page === 0}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50"
            >
              <ChevronsLeft size={20} />
            </button>
            <button
              onClick={() => onPage(lazyParams.page - 1)}
              disabled={lazyParams.page === 0}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="mx-4 text-sm">
              {t('page')} {lazyParams.page + 1} {t('of')} {Math.ceil(totalRecords / lazyParams.rows)}
            </span>
            <button
              onClick={() => onPage(lazyParams.page + 1)}
              disabled={lazyParams.page === Math.ceil(totalRecords / lazyParams.rows) - 1}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50"
            >
              <ChevronRight size={20} />
            </button>
            <button
              onClick={() => onPage(Math.ceil(totalRecords / lazyParams.rows) - 1)}
              disabled={lazyParams.page === Math.ceil(totalRecords / lazyParams.rows) - 1}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50"
            >
              <ChevronsRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {dialogVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 pt-20 pb-20 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl my-8">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">{editingBrand && editingBrand.id ? t('edit_brand') : t('new_brand')}</h2>
              <button onClick={hideDialog} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); saveBrand(); }} className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">{t('name')}</label>
                  <input
                    type="text"
                    id="name"
                    value={editingBrand?.name || ''}
                    onChange={(e) => setEditingBrand(prev => prev ? { ...prev, name: e.target.value } : null)}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="icon" className="block text-sm font-medium text-gray-700">{t('icon')}</label>
                  <div className="flex items-center space-x-4 mt-1">
                    {editingBrand?.icon && (
                      <img 
                        src={editingBrand.icon instanceof File ? URL.createObjectURL(editingBrand.icon) : `${process.env.REACT_APP_PIC_STORAGE}/${editingBrand.icon}`} 
                        alt="Brand Icon" 
                        className="w-16 h-16 object-contain rounded-full bg-gray-100 p-1 border border-gray-200" 
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Upload size={18} className="inline-block mr-2" />
                      {t('upload_icon')}
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      hidden
                      accept="image/*"
                      onChange={handleIconUpload}
                    />
                  </div>
                </div>

                {editingBrand && editingBrand.id !== 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('models')}</label>
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-lg font-semibold">{t('models_list')}</h3>
                      <button
                        type="button"
                        onClick={openNewModel}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200"
                      >
                        <Plus size={18} className="inline-block mr-2" />
                        {t('add_model')}
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('model')}</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('year')}</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('status')}</th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('actions')}</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {editingBrand.models?.map((model) => (
                            <tr key={model.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{model.modele}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{model.annee}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {model.active ? (
                                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                    {t('active')}
                                  </span>
                                ) : (
                                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                    {t('inactive')}
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  type="button"
                                  onClick={() => editModel(model)}
                                  className="text-indigo-600 hover:text-indigo-900 mr-2 focus:outline-none focus:underline"
                                >
                                  <Edit size={18} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => confirmDeleteModel(model)}
                                  className="text-red-600 hover:text-red-900 focus:outline-none focus:underline"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-sm text-gray-700">
                        {t('showing')} {((modelPagination.currentPage - 1) * modelPagination.perPage) + 1} - {Math.min(modelPagination.currentPage * modelPagination.perPage, modelPagination.total)} {t('of')} {modelPagination.total}
                      </span>
                      <div className="flex items-center">
                        <button
                        type='button'
                          onClick={() => handleModelPageChange(1)}
                          disabled={modelPagination.currentPage === 1}
                          className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50"
                        >
                          <ChevronsLeft size={20} />
                        </button>
                        <button
                        type='button'

                          onClick={() => handleModelPageChange(modelPagination.currentPage - 1)}
                          disabled={modelPagination.currentPage === 1}
                          className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50"
                        >
                          <ChevronLeft size={20} />
                        </button>
                        <span className="mx-4 text-sm">
                          {t('page')} {modelPagination.currentPage} {t('of')} {modelPagination.totalPages}
                        </span>
                        <button
                        type='button'

                          onClick={() => handleModelPageChange(modelPagination.currentPage + 1)}
                          disabled={modelPagination.currentPage === modelPagination.totalPages}
                          className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50"
                        >
                          <ChevronRight size={20} />
                        </button>
                        <button
                        type='button'

                          onClick={() => handleModelPageChange(modelPagination.totalPages)}
                          disabled={modelPagination.currentPage === modelPagination.totalPages}
                          className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50"
                        >
                          <ChevronsRight size={20} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={hideDialog}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modelDialogVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 pt-20 pb-20 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md my-8">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">{editingModel && editingModel.id ? t('edit_model') : t('new_model')}</h2>
              <button onClick={hideModelDialog} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); saveModel(); }} className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="modele" className="block text-sm font-medium text-gray-700">{t('model')}</label>
                  <input
                    type="text"
                    id="modele"
                    value={editingModel?.modele || ''}
                    onChange={(e) => setEditingModel(prev => prev ? { ...prev, modele: e.target.value } : null)}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="annee" className="block text-sm font-medium text-gray-700">{t('year')}</label>
                  <input
                    type="text"
                    id="annee"
                    value={editingModel?.annee || ''}
                    onChange={(e) => setEditingModel(prev => prev ? { ...prev, annee: e.target.value } : null)}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="active" className="flex items-center">
                    <input
                      type="checkbox"
                      id="active"
                      checked={editingModel?.active || false}
                      onChange={(e) => setEditingModel(prev => prev ? { ...prev, active: e.target.checked } : null)}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">{t('active')}</span>
                  </label>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={hideModelDialog}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

